/**
 * This component is a big hack to create a clickable <div>.
 * <button> (or even <a>) should ALWAYS be preferred for click interactions,
 * so this is a last resort to support clickable elements with complex descendants.
 *
 * Before using this component, ask yourself:
 *   - Can I use a <button>?
 *   - Can I use an <a>?
 *   - Can the contents inside the clickable area be organized
 *     strictly with phrasing content (i.e., <span>s as opposed to <div>s)?
 *   - How will this affect accessibility?
 *
 * Use this sparingly!!!!!
 */
import React, { useCallback } from 'react';
import classname from 'classnames';

interface ClickableDivProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  onClick: (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => void;
  tabIndex?: number;
  className?: string;
}

export default function ClickableDiv({
  children,
  onClick,
  tabIndex = 0,
  className,
  ...rest
}: ClickableDivProps) {
  const handleClick = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
    ) => {
      const { target } = e;
      // @ts-ignore
      target.setAttribute('aria-pressed', !target.getAttribute('aria-pressed'));
      onClick(e);
    },
    [onClick]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ([' ', 'Enter', 'Spacebar'].includes(e.key)) {
        handleClick(e);
      }
    },
    [handleClick]
  );

  return (
    <div
      aria-pressed={false}
      className={classname('ClickableDiv pointer', className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={tabIndex}
      {...rest}
    >
      {children}
    </div>
  );
}
